import { useMutation } from '@apollo/client';
import React from 'react';
import TransferPostOwnershipModal from './TransferPostOwnershipModal';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import Toast from '../../components/Toast';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { PlanTier, UserType } from '../../graphql/generated/graphql';
import useSeatLimits from '../../invitation/useSeatLimits';
import { UIActions } from '../../ui/uiSlice';
import UserProfileSettingsModal from '../../user/settings/UserProfileSettingsModal';
import { ALL_USERS_QUERY, useGetUser } from '../../user/useAllUsers';
import userActionPermissionCheck, {
  DEACTIVATE,
  DEMOTE_TO_USER,
  DOWNGRADE_TO_GUEST,
  EDIT_PROFILE,
  PROMOTE_TO_ADMIN,
  RESTORE_DEACTIVATED,
  TRANSFER_POST_OWNERSHIP,
  UPGRADE_TO_USER,
} from '../../user/userActionPermissionCheck';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';

const UPDATE_USER_TYPE_MUTATION = gql(/** @complexity 2 */ `
  mutation updateUserType($id: ID!, $type: UserType!) {
    updateUser(id: $id, type: $type) {
      id
      type
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const REACTIVATE_USER_MUTATION = gql(/** @complexity 4 */ `
  mutation reactivateUser($id: ID!) {
    reactivateUser(id: $id) {
      id
      deactivatedAt
      ...useAllUsers_user
    }
  }
`);

const DEACTIVATE_USER_MUTATION = gql(/** @complexity 2 */ `
  mutation deactivateUser($id: ID!, $newOwnerId: ID!) {
    deactivateUser(id: $id, newOwnerId: $newOwnerId) {
      id
      deactivatedAt
    }
  }
`);

const TRANSFER_POSTS_MUTATION = gql(/** @complexity 2 */ `
  mutation transferPosts($ownerId: ID!, $newOwnerId: ID!) {
    transferPostOwnership(ownerId: $ownerId, newOwnerId: $newOwnerId) {
      id
    }
  }
`);

const sentenceType = (type?: UserType | null) => {
  if (type === UserType.Admin) {
    return 'an admin';
  }
  if (type === UserType.Guest) {
    return 'a guest';
  }
  return 'a standard user';
};

const TEAMMATE_USER_MENU_USER_FRAGMENT = gql(`
  fragment TeammateUserMenu_user on User {
    id
    ...useGetUser_user
  }
`);

interface TeammateUserMenuProps {
  user: DocumentType<typeof TEAMMATE_USER_MENU_USER_FRAGMENT>;
}

const TeammateUserMenu = (props: TeammateUserMenuProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );
  const dispatch = useAppDispatch();
  const getUser = useGetUser();
  const user = getUser(props.user);
  const isCurrentUser = currentUser.id === user.id;
  const { isSeatLimitMaxed, isGuestLimitMaxed } = useSeatLimits();

  const [updateUserType] = useMutation(UPDATE_USER_TYPE_MUTATION);
  const [reactivateUser] = useMutation(REACTIVATE_USER_MUTATION);
  const [deactivateUser] = useMutation(DEACTIVATE_USER_MUTATION);
  const [transferPosts] = useMutation(TRANSFER_POSTS_MUTATION);

  const handleRestore = async (showUndo = true) => {
    await reactivateUser({
      variables: { id: user.id },
      update: legacyUpdateQueryList(
        { query: ALL_USERS_QUERY },
        'session.organization.users',
        'reactivateUser',
        true,
      ),
    });

    dispatch(
      UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
        <Toast
          level="success"
          message={`${user.name} has been restored as ${sentenceType(
            user.type,
          )}.`}
          onDismiss={toastProps.onDismiss}
          onSecondaryClick={() => {
            handleDeactivate(false);
            toastProps.onDismiss();
          }}
          secondaryText={showUndo ? 'Undo' : undefined}
        />
      )),
    );
  };

  const handleDeactivate = (showUndo = true) => {
    dispatch(
      UIActions.openModal<typeof TransferPostOwnershipModal>(
        TransferPostOwnershipModal,
        {
          user,
          deactivating: true,
          onConfirm: async (newOwner) => {
            await deactivateUser({
              variables: { id: user.id, newOwnerId: newOwner.id },
              update: legacyUpdateQueryList(
                { query: ALL_USERS_QUERY },
                'session.organization.users',
                'deactivateUser',
                false,
              ),
            });

            dispatch(
              UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
                <Toast
                  level="success"
                  message={`${user.name} has been deactivated and owned posts have been transferred to ${newOwner.name}.`}
                  onDismiss={toastProps.onDismiss}
                  onSecondaryClick={() => {
                    handleRestore(false);
                    toastProps.onDismiss();
                  }}
                  secondaryText={showUndo ? 'Reactivate' : undefined}
                />
              )),
            );
          },
        },
      ),
    );
  };

  const handleTransferPostOwnership = () => {
    dispatch(
      UIActions.openModal<typeof TransferPostOwnershipModal>(
        TransferPostOwnershipModal,
        {
          user,
          deactivating: false,
          onConfirm: async (newOwner) => {
            await transferPosts({
              variables: { ownerId: user.id, newOwnerId: newOwner.id },
            });

            dispatch(
              UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
                <Toast
                  level="success"
                  message={`Ownership of ${user.name}'s published posts has been
                transferred to ${newOwner.name}.`}
                  onDismiss={toastProps.onDismiss}
                />
              )),
            );
          },
        },
      ),
    );
  };

  const handleUserTypeUpdate = async (
    oldType: UserType,
    newType: UserType,
    showUndo = true,
  ) => {
    await updateUserType({ variables: { id: user.id, type: newType } });

    dispatch(
      UIActions.pushToast((toastProps: { onDismiss: () => void }) => (
        <Toast
          level="success"
          message={`${user.name} is now ${sentenceType(newType)}.`}
          onDismiss={toastProps.onDismiss}
          onSecondaryClick={() => {
            handleUserTypeUpdate(newType, oldType, false);
            toastProps.onDismiss();
          }}
          secondaryText={showUndo ? 'Undo' : undefined}
        />
      )),
    );
  };

  const isRestorationDisabled =
    user.type === UserType.Guest ? isGuestLimitMaxed : isSeatLimitMaxed;

  const isGuestUpgradableToUser =
    planTier === PlanTier.Free || !isSeatLimitMaxed;

  const restoreTooltip =
    planTier === PlanTier.Free
      ? 'Your team has reached\nthe seat limit for our free plan.\nPlease upgrade to a paid plan\nto add additional teammates.'
      : 'Seat limit reached.\nPlease contact support\nto adjust.';

  const menuItems = (
    [
      [
        TRANSFER_POST_OWNERSHIP,
        <MenuItem
          key="transferPostOwnership"
          onClick={() => handleTransferPostOwnership()}
          disabled={isRestorationDisabled}
        >
          Transfer post ownership
        </MenuItem>,
      ],
      [
        RESTORE_DEACTIVATED,
        <MenuItem
          key="restoreDeactivated"
          onClick={() => handleRestore(true)}
          disabled={isRestorationDisabled}
          tooltip={isRestorationDisabled ? restoreTooltip : undefined}
        >
          Restore as {sentenceType(user.type)}
        </MenuItem>,
      ],
      [
        EDIT_PROFILE,
        <MenuItem
          key="editProfile"
          onClick={() =>
            dispatch(
              UIActions.openModal(UserProfileSettingsModal, {
                user,
              }),
            )
          }
        >
          Edit profile
        </MenuItem>,
      ],
      [
        DEMOTE_TO_USER,
        <MenuItem
          key="demoteToUser"
          disabled={isCurrentUser}
          tooltip={isCurrentUser ? 'You cannot\ndemote yourself' : undefined}
          onClick={() => handleUserTypeUpdate(UserType.Admin, UserType.User)}
        >
          Demote to standard user
        </MenuItem>,
      ],
      [
        PROMOTE_TO_ADMIN,
        <MenuItem
          key="promoteToAdmin"
          onClick={() => handleUserTypeUpdate(UserType.User, UserType.Admin)}
        >
          Promote to admin
        </MenuItem>,
      ],
      [
        DOWNGRADE_TO_GUEST,
        <MenuItem
          key="downgradeToGuest"
          onClick={() => handleUserTypeUpdate(UserType.User, UserType.Guest)}
        >
          Downgrade to guest
        </MenuItem>,
      ],
      [
        UPGRADE_TO_USER,
        <MenuItem
          key="upgradeToUser"
          onClick={() => handleUserTypeUpdate(UserType.Guest, UserType.User)}
          disabled={!isGuestUpgradableToUser}
          tooltip={
            isGuestUpgradableToUser
              ? undefined
              : 'Seat limit reached.\nPlease contact support\nto adjust.'
          }
        >
          Upgrade to standard user
        </MenuItem>,
      ],
      [
        DEACTIVATE,
        <>
          <MenuSeparator />
          <MenuItem
            danger
            disabled={isCurrentUser}
            tooltip={
              isCurrentUser ? 'You cannot\ndeactivate yourself' : undefined
            }
            onClick={() => handleDeactivate(true)}
          >
            Deactivate
          </MenuItem>
        </>,
      ],
    ] as const
  )
    .filter(([action]) => userActionPermissionCheck(action, user, currentUser))
    .map(([action, item]) => (
      <React.Fragment key={action}>{item}</React.Fragment>
    ));

  return menuItems.length > 0 ? (
    <OverflowMenu iconSize={16} applyNegativeMargin={false}>
      {menuItems}
    </OverflowMenu>
  ) : null;
};

export default TeammateUserMenu;
